/**
 * "Start a Team on my tickets": the one-click Team, as logic.
 *
 * The Team this makes is the one the Add-a-Team page would make for a person
 * who switched GitHub on and picked one repository, with two changes: the
 * Team works from the creator's tickets (`ticketsAssignedTo: [viewer]`, the
 * Team's own setting since migration 0263). Bailey is on it, proposing from
 * those tickets each morning, and the create itself brings in the first of
 * them under Proposed (`desks/importOwnTickets.ts`), so the Team never opens
 * on an empty room. Accept on a case file is what starts planning. No admin,
 * no wizard, no second screen.
 *
 * Two reads answer every question the button has to ask first:
 *
 *   - `GET /api/connected-services`: which tracker the WORKSPACE has (Jira
 *     before Linear), and whether the reader has linked their own account to
 *     it. Personal rows the server returns are always the reader's own.
 *   - `GET /api/repos`: the repositories already added to the workspace, the
 *     only refs the create route can resolve.
 */
import type { CreateTeamLineInput } from "@yagni/shared";
import { CREATE_TEAM_LINE_KINDS, defaultLines } from "../desks/createTeamLines.js";
import { firstNameOf } from "../lib/names.js";

/** The server's refusal in its own words, else the fallback. */
async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.clone().json()) as { error?: unknown };
    if (typeof data?.error === "string" && data.error.length > 0) return data.error;
  } catch {
    // not JSON
  }
  return fallback;
}

export type StartTracker = "jira" | "linear";

export interface StartRepo {
  id: string;
  /** `owner/name`, the ref a repo attachment names. */
  ref: string;
}

export interface StartState {
  /** The workspace's tracker, Jira first; null when neither is connected. */
  tracker: StartTracker | null;
  /** Whether the reader has linked their own account on that tracker. */
  linked: boolean;
  repos: StartRepo[];
}

export interface StartViewer {
  id: string;
  displayName?: string | null;
}

export function trackerLabel(tracker: StartTracker): string {
  return tracker === "jira" ? "Jira" : "Linear";
}

const TRACKER_PREFERENCE: readonly StartTracker[] = ["jira", "linear"];

type ServiceRow = { serviceType?: unknown; connectionScope?: unknown };
type RepoRow = { id?: unknown; owner?: unknown; name?: unknown };

/** Read the tracker, the reader's link, and the workspace's repositories. */
export async function readStartState(fetchImpl: typeof fetch = fetch): Promise<StartState> {
  const [servicesRes, reposRes] = await Promise.all([
    fetchImpl("/api/connected-services", { credentials: "include" }),
    fetchImpl("/api/repos", { credentials: "include" }),
  ]);
  if (!servicesRes.ok) throw new Error(`connected-services ${servicesRes.status}`);
  if (!reposRes.ok) throw new Error(`repos ${reposRes.status}`);
  const services = (await servicesRes.json()) as unknown;
  const repoRows = (await reposRes.json()) as unknown;

  const rows: ServiceRow[] = Array.isArray(services) ? (services as ServiceRow[]) : [];
  // An absent scope is a workspace row (wire-compat with older backends).
  const isPersonal = (row: ServiceRow) => row.connectionScope === "personal";
  const tracker =
    TRACKER_PREFERENCE.find((kind) =>
      rows.some((row) => row.serviceType === kind && !isPersonal(row)),
    ) ?? null;
  const linked =
    tracker !== null && rows.some((row) => row.serviceType === tracker && isPersonal(row));

  const repos: StartRepo[] = [];
  if (Array.isArray(repoRows)) {
    for (const row of repoRows as RepoRow[]) {
      if (
        typeof row?.id === "string" &&
        typeof row.owner === "string" &&
        typeof row.name === "string"
      ) {
        repos.push({ id: row.id, ref: `${row.owner}/${row.name}` });
      }
    }
  }
  return { tracker, linked, repos };
}

export interface StartTeamBody {
  name: string;
  prompt: string;
  sources: string[];
  backlogMode: "proactive";
  attachments: Array<{ kind: "repo"; source: "github"; ref: string }>;
  workers: string[];
  lines: CreateTeamLineInput[];
  /** Whose tracker tickets the Team works from: the creator's. */
  ticketsAssignedTo: string[];
}

/**
 * The create request.
 *
 * Whose tickets rides at the top (`ticketsAssignedTo`), because it is the
 * Team's and lands whether or not any Agent is on it.
 *
 * Bailey rides `workers` so his propose line comes on (`backlogMode:
 * "proactive"` switches it on only for an engaged Bailey); the other lines on
 * (Wright, Proctor, Reeve) engage their own Agents on the server.
 */
export function buildStartTeamBody({
  viewer,
  tracker,
  repoRef,
}: {
  viewer: StartViewer;
  /** Null: no Linear or Jira; the Team works from tickets started in the rail. */
  tracker: StartTracker | null;
  repoRef: string;
}): StartTeamBody {
  const first = firstNameOf(viewer.displayName);
  // The wizard's lines for GitHub on, the way its submit sends them: every
  // offered kind on or off, Bailey's propose line on.
  const defaults = defaultLines({
    githubOn: true,
    backlogMode: "proactive",
    trackerPicked: false,
    fletcherAvailable: false,
  });
  const lines: CreateTeamLineInput[] = CREATE_TEAM_LINE_KINDS.map((kind) => ({
    kind,
    active: defaults[kind],
  }));
  return {
    name: first ? `${first}'s Team` : "My Team",
    prompt: tracker
      ? `Work the tickets assigned to me in ${trackerLabel(tracker)}.`
      : "Work the tickets I start in the rail.",
    sources: tracker ? ["github", tracker] : ["github"],
    backlogMode: "proactive",
    attachments: [{ kind: "repo", source: "github", ref: repoRef }],
    workers: ["bailey"],
    lines,
    ticketsAssignedTo: [viewer.id],
  };
}

export type StartTeamResult = { ok: true; id: string } | { ok: false; message: string };

export const START_TEAM_FAILED = "Could not start the Team. Try again.";

/** POST the Team. 201 with an id is the only success. */
export async function startTeam(
  body: StartTeamBody,
  fetchImpl: typeof fetch = fetch,
): Promise<StartTeamResult> {
  const result = await postTeam(body, fetchImpl);
  return result.ok ? { ok: true, id: result.id } : result;
}

/**
 * The one POST both one-click Teams share. On success it hands back the whole
 * 201 payload too, so a caller can read what the setup riding the create did
 * (`setup.workers.engaged`, `setup.lines.failed`).
 */
export async function postTeam(
  body: object,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: true; id: string; payload: unknown } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetchImpl("/api/desks", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, message: "Could not reach the server. Try again." };
  }
  if (res.status !== 201) {
    return { ok: false, message: await readErrorMessage(res, START_TEAM_FAILED) };
  }
  const payload = (await res.json().catch(() => null)) as { desk?: { id?: unknown } } | null;
  const id = payload?.desk?.id;
  if (typeof id !== "string" || !id) {
    return { ok: false, message: "The Team was created but did not come back with an id." };
  }
  return { ok: true, id, payload };
}
